"use client";

import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";

export default function CustomCursor() {
  const coreRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [cursorText, setCursorText] = useState("");
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    // Check matchMedia and ontouchstart to verify pointer support
    const checkSupport = () => {
      const hasHover = window.matchMedia("(hover: hover)").matches;
      const isFinePointer = window.matchMedia("(pointer: fine)").matches;
      const isTouch = ("ontouchstart" in window) || (navigator.maxTouchPoints > 0);

      setEnabled(hasHover && isFinePointer && !isTouch);
    };

    checkSupport();
    window.addEventListener("resize", checkSupport);
    return () => window.removeEventListener("resize", checkSupport);
  }, []);

  useEffect(() => {
    if (!enabled) return;

    // Track position
    const onMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;

      // Strong, immediate icon tracking
      gsap.to(coreRef.current, {
        x: clientX,
        y: clientY,
        duration: 0.15,
        ease: "power3.out",
      });
    };

    // Watch hover states globally
    const onMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      const cursorAttr = target.closest("[data-cursor]")?.getAttribute("data-cursor");
      const isInteractive = target.closest("button") || target.closest("a") || target.closest("[role='button']");

      if (cursorAttr === "view" || cursorAttr === "drag") {
        setCursorText(cursorAttr === "view" ? "VIEW" : "DRAG");
        gsap.to(coreRef.current, {
          width: 60,
          height: 60,
          backgroundColor: cursorAttr === "view" ? "#0d9488" : "#1c2c3c",
          scale: 1,
          rotation: 0,
          duration: 0.4,
          ease: "expo.out",
        });
        gsap.to(textRef.current, { opacity: 1, duration: 0.2, delay: 0.1 });
        coreRef.current?.classList.add("text-mode");
      } else if (isInteractive) {
        gsap.to(coreRef.current, {
          scale: 1, // Do not increase size at all
          rotation: 45, // Gentle tilt for interaction
          duration: 0.4,
          ease: "power2.out",
        });
      }
    };

    const onMouseOut = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      gsap.to(textRef.current, { opacity: 0, duration: 0.1 });
      gsap.to(coreRef.current, {
        width: 16,
        height: 16,
        backgroundColor: "transparent",
        scale: 1,
        rotation: 0,
        duration: 0.5,
        ease: "expo.out",
      });
      coreRef.current?.classList.remove("text-mode");
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseover", onMouseOver);
    window.addEventListener("mouseout", onMouseOut);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseover", onMouseOver);
      window.removeEventListener("mouseout", onMouseOut);
    };
  }, [enabled]);

  if (!enabled) return null;

  return (
    <>
      <div ref={coreRef} className="custom-cursor-core">
        <span ref={textRef} className="custom-cursor-text">
          {cursorText}
        </span>
      </div>
    </>
  );
}
